import Button from 'assets/components/button/Button';
import LinkButton from 'assets/components/button/LinkButton';
import ThemeButtonLink from 'assets/components/button/themes/Theme.Button.Link';
import DataMap from 'assets/components/dataMap/DataMap';
import Icon from 'assets/components/icon/Icon';
import ErrorWrapper from 'assets/components/inputs/wrappers/ErrorWrapper';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import { getLocales } from 'assets/locales/Locale';
import Gallery from 'assets/templates/gallery/Gallery';
import Theme from 'assets/themes/Theme.Common';
import { combineStrings } from 'assets/utils/data/String';
import {
  getFileExtensionFromFileName,
  isImageExtension,
  isValidValue,
} from 'assets/utils/parsersAndValidation/Validators';
import { isEmpty } from 'lodash';

type PreviewDocument = {
  id: number;
  name: string;
  url?: string;
  extension?: string;
  description?: string;
  documentName?: string;
  invoiceNumber?: string;
  invoiceDate?: string;
  approved?: boolean;
  mimeType?: string;
  storedFilename?: string;
  documentType: string;
  onDelete?(): void;
  onEdit?(doc: PreviewDocument): void;
};
type Props = {
  documents: PreviewDocument[];
  children?: any;
  isEditDisabled?: boolean;
  isDownloadDisabled?: boolean;
  isDeleteDisabled?: boolean;
  showGallery?: boolean;
};
export default function DocumentPreview(props: Props) {
  const { lang } = getLocales();

  const hasInvoices = props.documents?.some((it) => it?.invoiceDate || it?.invoiceNumber);
  const hasDocumentName = props.documents?.some((it) => it?.documentName);
  const hasDescription = props.documents?.some((it) => it?.description);

  const images =
    props.showGallery &&
    props.documents
      ?.filter((it) => isImageExtension(it.extension))
      ?.map(
        (it) =>
          ({
            url: it.url,
            thumbnail: it.url,
            alt: combineStrings(' | ', it?.storedFilename ?? it?.name, it?.documentName),
            extension: it.extension,
            description: it.description,
            mime_type: it.mimeType,
          }) as Component.Media.Import['media']
      );
  return (
    <>
      {props.children}
      {!props.documents?.length ? (
        <ErrorWrapper children={lang.noFiles} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <table className={ThemeTableSimple}>
            <thead>
              <tr>
                <th>{lang.fileName}</th>
                {hasDocumentName && <th>{lang.documentName}</th>}
                {hasInvoices && (
                  <>
                    <th>{lang.invoiceNumber}</th>
                    <th>{lang.invoiceDate}</th>
                  </>
                )}
                <th>{lang.documentType}</th>
                {hasDescription && <th>{lang.notes}</th>}
                <th data-actions>{lang.actions}</th>
              </tr>
            </thead>
            <tbody>
              <DataMap
                data={props.documents}
                render={({ data: doc, index }) => (
                  <tr key={doc.id ?? `index-${index}`}>
                    <td>
                      {doc.name}
                      &nbsp;
                      {isValidValue(doc.approved) && !!doc.url && (
                        <Icon
                          style={{
                            color: doc.approved ? Theme.Color.success : Theme.Color.error,
                          }}
                          class={doc.approved ? 'fas-check' : 'fas-times'}
                        />
                      )}
                    </td>
                    {hasDocumentName && (
                      <>
                        <td>{doc.documentName}</td>
                      </>
                    )}
                    {hasInvoices && (
                      <>
                        <td>{doc.invoiceNumber}</td>
                        <td>{doc.invoiceDate}</td>
                      </>
                    )}
                    <td>
                      {isImageExtension(doc.extension ?? getFileExtensionFromFileName(doc?.name)) ||
                      doc.mimeType?.startsWith('image')
                        ? 'Image'
                        : doc.documentType}
                    </td>
                    {hasDescription && <td>{doc.description ?? lang.noNotes}</td>}
                    <td>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        {(doc.id || doc.url) && (
                          <LinkButton
                            class={ThemeButtonLink}
                            url={doc.url}
                            target="_blank"
                            disabled={props.isDownloadDisabled || !doc.url}
                          >
                            {lang.download}
                          </LinkButton>
                        )}
                        {doc.onEdit && (
                          <Button
                            class={ThemeButtonLink}
                            disabled={props.isEditDisabled}
                            onClick={() => doc.onEdit(doc)}
                          >
                            {lang.edit}
                          </Button>
                        )}
                        {doc.onDelete && (
                          <Button
                            class={ThemeButtonLink}
                            disabled={props.isDeleteDisabled}
                            onClick={doc.onDelete}
                            style={{ color: Theme.Color.error }}
                          >
                            {lang.delete}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              />
              {props?.showGallery && !isEmpty(images) && (
                <tr>
                  <td colSpan={3 + Number(!!hasDocumentName) + (hasInvoices ? 2 : 0)}>
                    <Icon class="fas-images" />
                    &nbsp;{lang.gallery}: {images.length}
                  </td>
                  <td>
                    <Gallery
                      media={images}
                      title={lang.gallery}
                      actions={(data) => (
                        <Button
                          class={ThemeButtonLink}
                          render={lang.showGallery}
                          onClick={() => data.toggle({ isOpen: true })}
                          htmlElementProps={{ title: lang.open }}
                        />
                      )}
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
